import type { UserChangeRequestReason } from '@contracts/changeRequest/ChangeRequestContract';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuthenticatedUser } from '@src/authentication/useAuthenticatedUser';
import { BaseModal } from '@src/common/components/BaseModal';
import { Divider } from '@src/common/components/Divider';
import type { FormDropDownOptions } from '@src/common/components/FormDropdown';
import { FormDropdown } from '@src/common/components/FormDropdown';
import { CloseConfirmModalFooter } from '@src/common/components/modal/CloseConfirmModalFooter';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { theme } from '@src/common/styles/Theme';
import { AddNoteForm } from '@src/notes/AddNoteForm';
import { AddNoteSchema } from '@src/notes/AddNoteSchema';
import { NoteContext } from '@src/notes/domain/NoteContext';
import { useAddNote } from '@src/notes/hooks/useAddNote';
import { NoteContentField, NoteContextField } from '@src/notes/NoteFields';
import { DetailsItem } from '@src/orders/components/DetailsItem';
import {
  ChangeRequestReasonField,
  ChangeRequestSchema,
} from '@src/orders/components/orderActionsMenu/modal/schema/ChangeRequestSchema';
import type { OrderListing } from '@src/orders/domain/OrderList';
import { type SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

const CombinedSchema = yup.object().shape({
  ...AddNoteSchema.fields,
  ...ChangeRequestSchema.fields,
});

type CombinedSchemaType = yup.InferType<typeof CombinedSchema>;

type Props = {
  open: boolean;
  onClose: () => void;
  onSuccess: (reason: UserChangeRequestReason) => void;
  onError: () => void;
  order: OrderListing;
};

export const ChangeRequestModal = ({
  open,
  onClose,
  onSuccess,
  onError,
  order,
}: Props) => {
  const { t } = useTranslation();
  const { user } = useAuthenticatedUser();

  const form = useForm<CombinedSchemaType>({
    resolver: yupResolver(CombinedSchema),
    defaultValues: {
      [NoteContextField.fieldName]: [NoteContext.adjustments.valueOf()],
      [ChangeRequestReasonField.fieldName]: '',
    },
  });

  const { addNote } = useAddNote({
    onSuccess: () => {
      const [selectedReason] = form.getValues([
        ChangeRequestReasonField.fieldName,
      ]);
      onSuccess(selectedReason as UserChangeRequestReason);
      onClose();
    },
    onError: () => onError(),
  });

  const changeRequestReason: FormDropDownOptions = [
    { value: 'AGENT_ERROR', label: t('changeRequest.agentError') },
    { value: 'CUSTOMER_REQUEST', label: t('changeRequest.customerRequest') },
    { value: 'ADD_NEW_PERMIT', label: t('changeRequest.newPermit') },
    { value: 'STATE_ERROR', label: t('changeRequest.stateError') },
  ];

  const confirmChangeRequest: SubmitHandler<CombinedSchemaType> = (data) => {
    addNote({
      author: user.id,
      tags: data[NoteContextField.fieldName] as string[],
      content: data[NoteContentField.fieldName] as string,
      parent: { id: order.id, type: 'ORDER' },
    });
  };

  return (
    <BaseModal open={open} onClose={onClose} title={t('changeRequest.title')}>
      <Stack spacing={theme.spacing['4']}>
        <DetailsItem
          itemName={t('client')}
          value={order.client.doingBusinessAs || order.client.name}
          labelVariant="subtitle2"
        />
        <DetailsItem
          itemName={t('orderSummary.orderNumber')}
          value={order.displayId}
          labelVariant="subtitle2"
        />
        <DetailsItem
          labelVariant="subtitle2"
          itemName={t('orderSummary.createdOn')}
          value={order.createdAt.formatDate()}
        />
        <Typography>{t('changeRequest.instructions')}</Typography>
      </Stack>
      <form>
        <Stack padding={theme.spacing['6']} gap={theme.spacing['6']}>
          <FormDropdown
            fieldName={ChangeRequestReasonField.fieldName}
            control={form.control}
            label={t('changeRequest.reason')}
            options={changeRequestReason}
          />

          <AddNoteForm
            control={form.control}
            context={NoteContext.adjustments}
            placeholder={t('changeRequest.noteInstructions')}
            setValue={form.setValue}
          />
          <Divider style={{ marginTop: theme.spacing['6'] }} />
        </Stack>
        <CloseConfirmModalFooter
          onClose={onClose}
          confirm={form.handleSubmit(confirmChangeRequest)}
        />
      </form>
    </BaseModal>
  );
};
